import config from '@/modules/config/functions/config';
import UserChangeCredentialModal from '@/modules/user-management/components/user-change-credential-modal';
import UserDisableModal from '@/modules/user-management/components/user-disable-modal';
import UserPromotionManagementModal from '@/modules/user-management/components/user-promotion-management-modal';
import useUsersQuery from '@/modules/users/queries/useUsersQuery';
import type { User } from '@/modules/users/types/User';
import { Helmet } from '@dr.pogodin/react-helmet';
import { createFileRoute, notFound } from '@tanstack/react-router';
import {
  Button,
  Card,
  Dropdown,
  Empty,
  Input,
  isEmptyString,
  Modal,
  Pagination,
  Skeleton,
  Table,
  Tooltip,
  Typography,
} from '@thisisdmg/tirecloud-patterns/v2';
import {
  IconActions,
  IconClose,
  IconEdit,
  IconPlatforms,
} from '@thisisdmg/tirecloud-patterns/v2/components/icons';
import type { MenuItemType } from 'antd/es/menu/interface';
import { type } from 'arktype';
import { debounce } from 'lodash-es';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { match, P } from 'ts-pattern';
import classes from './index.module.css';

const PAGE_SIZE = config('misc.dataTablePageSize', 10);

function UsersPage() {
  const { t } = useTranslation();
  const p = Route.useSearch({ select: (s) => s.p });
  const q = Route.useSearch({ select: (s) => s.q });
  const navigate = Route.useNavigate();

  const [doingLiveSearch, setDoingLiveSearch] = useState(false);
  const [tmpSearchParamState, setTmpSearchParamState] = useState(q);
  const { data: users, isLoading } = useUsersQuery({
    variables: {
      query: {
        userNumber: q,
        offset: ((p ?? 1) - 1) * PAGE_SIZE,
        limit: PAGE_SIZE,
      },
    },
  });

  useEffect(() => {
    setDoingLiveSearch(true);
    const debounceState = debounce(() => {
      const trimmedSearchQuery = tmpSearchParamState?.trim() ?? '';
      navigate({
        search: (prev) => ({
          ...prev,
          p: 1,
          q: trimmedSearchQuery.length > 0 ? trimmedSearchQuery : undefined,
        }),
      });
      setDoingLiveSearch(false);
    }, 500);
    debounceState();
    return () => debounceState.cancel();
  }, [tmpSearchParamState, navigate]);

  const [userToBeDisabled, setUserToBeDisabled] = useState<number | undefined>(
    undefined
  );
  const [
    userPromotionManagementModalState,
    setUserPromotionManagementModalState,
  ] = useState<
    | {
        open: false;
      }
    | {
        open: true;
        userId: number;
        mode: 'disable' | 'enable';
      }
  >({
    open: false,
  });

  const [changeCredentialModalState, setChangeCredentialModalState] = useState<{
    userId?: number;
    open: boolean;
    username?: string;
  }>({
    open: false,
  });

  const [showHowToEnableUser, setShowHowToEnableUser] = useState(false);

  return (
    <>
      <Helmet>
        <title>{t('user_management.title')}</title>
      </Helmet>
      <Modal
        open={showHowToEnableUser}
        onCancel={() => setShowHowToEnableUser(false)}
        title={t('user_management.how_to_enable_user_title')}
        footer={null}
        closeButtonProps={{ label: t('common.close') }}
      >
        <Typography variant="body">
          {t('user_management.user_already_disabled')}
        </Typography>
      </Modal>
      <div>
        <div className={classes.user_management_header}>
          <div>
            <Typography
              variant="hero"
              className={classes.user_management_header__title}
            >
              {t('user_management.title')}
            </Typography>
          </div>
          <div className={classes.user_management_header__search}>
            <Input
              placeholder={t('common.search')}
              value={tmpSearchParamState}
              onChange={(e) => setTmpSearchParamState(e.target.value)}
            />
          </div>
        </div>
        {config('features.userManagement.canDisableUser') === true &&
          userToBeDisabled !== undefined && (
            <UserDisableModal
              userId={userToBeDisabled}
              onClose={() => setUserToBeDisabled(undefined)}
            />
          )}
        {config('features.userManagement.canUpdateCredentials') === true &&
          changeCredentialModalState.userId !== undefined && (
            <UserChangeCredentialModal
              open={changeCredentialModalState.open}
              userId={changeCredentialModalState.userId}
              username={changeCredentialModalState.username ?? ''}
              onClose={() => {
                setChangeCredentialModalState({
                  open: false,
                  userId: undefined,
                  username: undefined,
                });
              }}
            />
          )}
        {config('features.userManagement.canDisableUserPromotions') === true &&
          userPromotionManagementModalState.open === true && (
            <UserPromotionManagementModal
              userId={userPromotionManagementModalState.userId}
              mode={userPromotionManagementModalState.mode}
              onClose={() =>
                setUserPromotionManagementModalState({ open: false })
              }
            />
          )}
        <Table<User>
          className={classes.user_list_desktop}
          dataSource={isLoading || doingLiveSearch ? [] : (users?.data ?? [])}
          pagination={false}
          rowKey="id"
          locale={{
            emptyText:
              isLoading || doingLiveSearch ? <Skeleton active /> : <Empty />,
          }}
          columns={[
            {
              title: t('common.name'),
              key: 'name',
              render(_, record) {
                return record.fullName ?? 'N/A';
              },
            },
            {
              title: t('user.user_number'),
              dataIndex: 'userNumber',
              key: 'userNumber',
            },
            {
              title: t('user.username'),
              dataIndex: 'username',
              key: 'username',
              render(_, record: User) {
                return record.username ?? 'N/A';
              },
            },
            {
              title: t('user.email'),
              key: 'email',
              render(_, record: User) {
                return record.email ?? 'N/A';
              },
            },
            {
              title: t('user.phone_number'),
              key: 'phoneNumber',
              render(_, record: User) {
                return record.phoneNumber ?? 'N/A';
              },
            },
            {
              title: t('user.company_name'),
              key: 'companyName',
              render(_, record: User) {
                return record.company ?? 'N/A';
              },
            },
            {
              title: t('user.job_title'),
              key: 'jobTitle',
              render(_, record: User) {
                return record.jobTitle ?? 'N/A';
              },
            },
            {
              title: t('common.active_user'),
              key: 'activeUser',
              render(_, record) {
                return record.published === true
                  ? t('common.yes')
                  : t('common.no');
              },
            },
            {
              hidden:
                config('features.userManagement.canUpdateCredentials') !==
                  true &&
                config('features.userManagement.canDisableUser') !== true,
              title: t('common.actions'),
              render: (_, item: User) => {
                return (
                  <div className="tc:flex tc:gap-x-2">
                    {config('features.userManagement.canUpdateCredentials') ===
                      true && (
                      <Tooltip title={t('common.edit')} arrow placement="top">
                        <Button.Solid
                          icon={<IconEdit />}
                          onClick={() => {
                            setChangeCredentialModalState({
                              userId: item.id,
                              open: true,
                              username: item.username ?? '',
                            });
                          }}
                          buttonClass="primary"
                        >
                          {t('common.edit')}
                        </Button.Solid>
                      </Tooltip>
                    )}
                    <Dropdown
                      useCustomTriggerButton
                      menu={{
                        items: (
                          [
                            {
                              type: 'item',
                              key: 'block_user',
                              label:
                                item.published === false
                                  ? t('common.disabled')
                                  : t('user_management.block_user'),
                              icon: <IconClose />,
                              className:
                                item.published !== false
                                  ? 'tc:text-feedback-red-500'
                                  : undefined,
                              onClick: () => {
                                if (item.published === false) {
                                  setShowHowToEnableUser(true);
                                  return;
                                }
                                setUserToBeDisabled(item.id);
                              },
                              hidden:
                                config(
                                  'features.userManagement.canDisableUser'
                                ) !== true,
                            },
                            {
                              type: 'item',
                              key: 'exclude_from_promotions',
                              label:
                                item.notEligibleForPromotions === true
                                  ? t('user_management.enable_promotions')
                                  : t('user_management.disable_promotions'),
                              icon: <IconPlatforms />,
                              onClick: () => {
                                setUserPromotionManagementModalState({
                                  open: true,
                                  userId: item.id,
                                  mode:
                                    item.notEligibleForPromotions === true
                                      ? 'enable'
                                      : 'disable',
                                });
                              },
                              hidden:
                                config(
                                  'features.userManagement.canDisableUserPromotions'
                                ) !== true,
                            },
                          ] as Array<MenuItemType & { hidden?: boolean }>
                        ).filter(
                          (item) => item.hidden !== true
                        ) as MenuItemType[],
                      }}
                    >
                      <Button.Solid
                        icon={<IconActions />}
                        label={t('common.more')}
                      />
                    </Dropdown>
                  </div>
                );
              },
            },
          ]}
        />
        <div className={classes.user_list_mobile}>
          {isLoading ? (
            <Skeleton active />
          ) : (
            users?.data.map((user) => {
              return (
                <Card
                  key={user.id}
                  classNames={{
                    body: classes.user_list_mobile__card_body,
                  }}
                >
                  {!isEmptyString(user.fullName) && (
                    <Typography
                      variant="heading-3"
                      className={classes.user_list_mobile__card_body__title}
                    >
                      {user.fullName}
                    </Typography>
                  )}
                  <Typography variant="body">
                    {t('user.email')}: {user.email ?? 'N/A'}
                  </Typography>
                  <Typography variant="body">
                    {t('user.phone_number')}: {user.phoneNumber ?? 'N/A'}
                  </Typography>
                  <Typography variant="body">
                    {t('user.company_name')}: {user.company ?? 'N/A'}
                  </Typography>
                  <Typography variant="body">
                    {t('user.job_title')} {user.jobTitle ?? 'N/A'}
                  </Typography>
                  <div className={classes.user_list_mobile__actions}>
                    <Tooltip title={t('common.edit')} arrow placement="top">
                      <Button.Solid
                        icon={<IconEdit />}
                        onClick={() => {
                          setChangeCredentialModalState({
                            userId: user.id,
                            open: true,
                            username: user.username ?? '',
                          });
                        }}
                      >
                        {t('common.edit')}
                      </Button.Solid>
                    </Tooltip>
                    <Tooltip
                      title={
                        user.published === false
                          ? t('user_management.user_already_disabled')
                          : t('user_management.unblock_user')
                      }
                      arrow
                      placement="top"
                    >
                      <div>
                        <Button.Solid
                          icon={<IconClose />}
                          disabled={user.published === false}
                          onClick={() => {
                            setUserToBeDisabled(user.id);
                          }}
                          className={
                            user.published !== false
                              ? 'tc:bg-feedback-red-500'
                              : undefined
                          }
                        >
                          {user.published === false
                            ? t('user_management.unblock_user')
                            : t('user_management.block_user')}
                        </Button.Solid>
                      </div>
                    </Tooltip>
                  </div>
                </Card>
              );
            })
          )}
        </div>
        <div className={classes.pagination}>
          <Pagination
            current={p ?? 1}
            pageSize={PAGE_SIZE}
            total={users?.meta?.pagination?.total ?? 0}
            onChange={(page) => {
              navigate({
                search: (prev) => ({
                  ...prev,
                  p: page,
                }),
              });
            }}
            showSizeChanger={false}
            hideOnSinglePage
          />
        </div>
      </div>
    </>
  );
}

export const Route = createFileRoute('/_main-ui/_protected/user-management/')({
  component: UsersPage,
  validateSearch: type({
    'q?': 'string',
    'p?': type.string
      .or(type.number)
      .or(type.undefined)
      .pipe((s) =>
        match(s)
          .with(P.number, (x) => Math.max(1, x))
          .with(P.string, (x) => Math.max(1, ~~x))
          .otherwise(() => 1)
      ),
  }),
  loader: async () => {
    if (config('features.userManagement.enabled') !== true) throw notFound();
  },
});
